import axios from "axios";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Col, Row, Button, Modal } from "react-bootstrap";
import ErrorHandler from "../../../components/core/ErrorHandler";
import { LoadingSpinner } from "../../../components/layout/LoadingSpinner";
import Accordion from "../../../components/Accordion";
import AdminRemittanceLookupForm from "./AdminRemittanceLookupForm";
import SharedMainSelectableClientGrid from "../../../shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import {
    Base64ToArrayBuffer,
    SaveByteArray,
} from "shared/helpers/Base64ArrayTranslator";
import { ZIndexContext } from "@progress/kendo-react-common";

import {
    DefaultColumnWidth,
    DefaultDateColumnWidth,
    DefaultNoFilterColumnWidth,
    DefaultBooleanOrShortWidthColumnWidth
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";
import { LogError } from "../../../components/AppInsights/TelemetryService";
import {
    GridColumn,
} from "@progress/kendo-react-grid";
import {
    MoneyCell,
    AggregateMoneyFooterCell,
} from "../../../components/grid/Kendo/CustomKendoGridCells";
import { useSuppressCheck } from "./hooks/useSuppressCheck";
import { useUnsuppressCheck } from "./hooks/useUnsuppressCheck";
import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import useGridButtons from "shared/components/grids/hooks/useGridButtons";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";
import "./AdminRemittance.css";

export default function AdminRemittanceLookup(props) {
    const { passedProps } = props;
    const { invalidateReactQueryKey } = useReactQueryHelpers();
    
    const user = passedProps.user.email;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({});
    const [expanded, setExpanded] = useState(false);
    const _container = React.useRef();
    const clientGridRef = useRef();
    const sendAccordionParent = (props) => {
        setExpanded(props);
    };
    const [submittedFormData, setSubmittedFormData] = useState(null);
    const [mainGridData, setMainGridData] = useState([]);
    const [mainGridAggregates, setMainGridAggregates] = useState([]);
    const [reloadCheckConfirmation, setReloadCheckConfirmationModel] = useState(false);
    const [deleteCheckConfirmation, setDeleteCheckConfirmationModel] = useState(false);
    const [suppressCheckConfirmation, setSuppressCheckConfirmationModel] = useState(false);
    const [unsuppressCheckConfirmation, setUnsuppressCheckConfirmationModel] = useState(false);
    const DATA_ITEM_KEY = "pmid";
    const { AddGridButton, gridButtons } = useGridButtons();
    const {
        gridDataStateResetProcessingInitiated,
        InitiateGridDataStateReset,
        GridDataStateResetCompleted: onGridDataStateResetCompleted,
    } = useGridDataStateResetProcessing();

    // Initialize hooks
    const suppressMutation = useSuppressCheck();
    const unsuppressMutation = useUnsuppressCheck();

    const gridApiParams = useMemo(() => {
        if (IsNullOrEmpty(user)) {
            return {};
        }

        if (!submittedFormData) {
            return {};
        }

        let params = {
            user: user,
            checkAmount: submittedFormData.checkAmount,
            parentCompantyID: submittedFormData.selectedParentCompany,
            nCPDP: submittedFormData.selectedLocations,
            checkNumber: submittedFormData.checkNumber,
            startDate: submittedFormData.startDate,
            endDate: submittedFormData.endDate,
            suppressTypeId: submittedFormData.suppressTypeId,
            payerId: submittedFormData.selectedPayers,
            dateTypeId: submittedFormData.dateTypeId,
            fileName: submittedFormData.fileName,
        };
        return params;
    }, [submittedFormData, user]);

    useEffect(() => {
        AddGridButton(
            "Reload Check",
            handleForReloadCheck,
            true
        );
        AddGridButton(
            "Delete Check",
            handleForDeleteCheck,
            true
        );
        AddGridButton(
            "Suppress Check",
            handleForSuppressCheck,
            true
        );
        AddGridButton(
            "Unsuppress Check",
            handleForUnSuppressCheck,
            true
        );
    }, []);

    function handleFormSubmit(formInput) {
        InitiateGridDataStateReset();
        setSubmittedFormData(formInput);
    }

    function handleForReloadCheck() {
        setReloadCheckConfirmationModel(true);
    }

    function handleReloadCheckConfirmationYesButton() {
        setReloadCheckConfirmationModel(false);
        saveReloadCheck();
    }

    function handleForDeleteCheck() {
        setDeleteCheckConfirmationModel(true);
    }

    function handleDeleteCheckConfirmationYesButton() {
        setDeleteCheckConfirmationModel(false);
        deleteCheck();
    }

    function handleForSuppressCheck() {
        setSuppressCheckConfirmationModel(true);
    }

    function handleSuppressCheckConfirmationYesButton() {
        setSuppressCheckConfirmationModel(false);
        const selectedDataKeys = clientGridRef.current
            ? clientGridRef.current.GetSelectedGridDataKeys()
            : null;

        if (!selectedDataKeys || selectedDataKeys.length <= 0) {
            alert("Please select at least one record to suppress check");
            return;
        }

        const allData = clientGridRef.current
            ? clientGridRef.current.GetAllGridData()
            : [];
        const selectedItems = allData.filter((item) => selectedDataKeys.includes(item[DATA_ITEM_KEY]));

        suppressMutation.mutate(
            {
                selectedItems,
                submittedFormData,
                user: user,
            },
            {
                onSuccess: (data) => {
                    onSuccess({ data, config: { url: "api/RemittanceLookup/AdminRemittanceSuppressChecks" } });
                },
                onError: (error) => {
                    onFailure(error);
                },
            }
        );
    }

    function handleForUnSuppressCheck() {
        setUnsuppressCheckConfirmationModel(true);
    }

    function onSuccessCallbackForDataUpdate() {
        setIsLoading(false);
        //NOTE: Here, invalidate any necessary keys (this should trigger a data refetch), reset any states/values
        invalidateReactQueryKey("api/RemittanceLookup/GetAdminRemittanceReport");
    }

    function handleUnSuppressCheckConfirmationYesButton() {
        setUnsuppressCheckConfirmationModel(false);
        const selectedDataKeys = clientGridRef.current
            ? clientGridRef.current.GetSelectedGridDataKeys()
            : null;

        if (!selectedDataKeys || selectedDataKeys.length <= 0) {
            alert("Please select at least one record to unsuppress check");
            return;
        }

        const allData = clientGridRef.current
            ? clientGridRef.current.GetAllGridData()
            : [];
        const selectedItems = allData.filter((item) => selectedDataKeys.includes(item[DATA_ITEM_KEY]));

        unsuppressMutation.mutate(
            {
                selectedItems,
                submittedFormData,
                user: user
            },
            {
                onSuccess: (data) => {
                    onSuccess({ data, config: { url: "api/RemittanceLookup/AdminRemittanceUnsuppressChecks" } });
                },
                onError: (error) => {
                    onFailure(error);
                },
            }
        );
    }

    function refrehGridActiveColors() {
        var grid = _container.current.querySelector(".k-table-tbody");
        if (grid) {
            var rows = grid.querySelectorAll("tr");
            rows.forEach((row) => {
                var colorChanges = "applyRedColor";
                var cells = row.querySelectorAll("td");
                var applyColor = false;
                cells.forEach((cell) => {
                    if (cell.className.indexOf("redcolor") > 0) {
                        applyColor = true;
                    }
                    if (applyColor && cell.className.indexOf("positiveCurrency") > 0) {
                        cell.className = cell.className.replace("positiveCurrency", "");
                    }
                });
                if (applyColor) {
                    row.className = row.className + " " + colorChanges;
                } else {
                    row.className = row.className.replace(colorChanges, "");
                }
            });
        }
    }

    function handleFormReset() {
        setMainGridData([]);
        setSubmittedFormData(null);
        if (clientGridRef.current) {
            clientGridRef.current.ResetGridDataState();
        }
    }

    function onSuccess(response) {
        switch (response.config.url) {
            case "api/RemittanceLookup/AdminRemittanceReloadChecks":
                onSuccessCallbackForDataUpdate();
                break;
            case "api/RemittanceLookup/AdminRemittanceDeleteChecks":
                onSuccessCallbackForDataUpdate();
                break;
            case "api/RemittanceLookup/AdminRemittanceSuppressChecks":
                onSuccessCallbackForDataUpdate();
                break;
            case "api/RemittanceLookup/AdminRemittanceUnsuppressChecks":
                onSuccessCallbackForDataUpdate();
                break;
            default:
                break;
        }
    }

    function saveReloadCheck() {
        const selectedDataKeys = clientGridRef.current
            ? clientGridRef.current.GetSelectedGridDataKeys()
            : null;

        if (!selectedDataKeys || selectedDataKeys.length <= 0) {
            alert("Please select at least one record to reload check");
            return;
        }

        const allData = clientGridRef.current
            ? clientGridRef.current.GetAllGridData()
            : [];
        var currentItem = allData.filter((item) => selectedDataKeys.includes(item[DATA_ITEM_KEY]));

        let accessToken = passedProps.auth.getAccessToken();
        setIsLoading(true);
        try {
            var formatSelectedItem = currentItem.map((item, i) => {
                return {
                    id: i,
                    pMID: item.pmid,
                    rPID: item.pM_RPID,
                    pBID: item.pB_PBID,
                    checkNo: item.checkNumber,
                    checkDate: item.checkDate,
                    checkAmt: item.checkAmount,
                    fileName: item.filename,
                    queueStatus: "A",
                };
            });

            let response = axios
                .post(
                    "api/RemittanceLookup/AdminRemittanceReloadChecks",
                    {
                        user: user,
                        rpid: null,
                        comments: "",
                        queueID: null,
                        newRawFileName: "",
                        status: "3",
                        checkedItems: JSON.stringify(formatSelectedItem),
                    },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                )
                .then(onSuccess)
                .catch(onFailure);
        } catch (exception) {
            onFailure(exception);
        }
    }

    function deleteCheck() {
        const selectedDataKeys = clientGridRef.current
            ? clientGridRef.current.GetSelectedGridDataKeys()
            : null;

        if (!selectedDataKeys || selectedDataKeys.length <= 0) {
            alert("Please select at least one record to delete check");
            return;
        }

        const allData = clientGridRef.current
            ? clientGridRef.current.GetAllGridData()
            : [];
        var currentItem = allData.filter((item) => selectedDataKeys.includes(item[DATA_ITEM_KEY]));

        let accessToken = passedProps.auth.getAccessToken();
        setIsLoading(true);
        try {
            var formatSelectedItem = currentItem.map((item, i) => {
                return {
                    id: i,
                    pMID: item.pmid,
                    rPID: item.pM_RPID,
                    pBID: item.pB_PBID,
                    checkNo: item.checkNumber,
                    checkDate: item.checkDate,
                    checkAmt: item.checkAmount,
                    fileName: item.filename,
                    queueStatus: item.exportStatus,
                };
            });

            let response = axios
                .post(
                    "api/RemittanceLookup/AdminRemittanceDeleteChecks",
                    {
                        user: user,
                        rpid: submittedFormData.selectedParentCompany,
                        Notes: "",
                        checkedItems: JSON.stringify(formatSelectedItem),
                    },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                )
                .then(onSuccess)
                .catch(onFailure);
        } catch (exception) {
            onFailure(exception);
        }
    }

    function onDownloadFileFailure(fileType) {
        setIsLoading(false);
        let msg = `Unable to download ${fileType} payment details.`;
        alert(msg);
    }

    function GetDownloadFileControllerName(fileType) {
        switch (fileType.toLowerCase()) {
            case "rawfile":
                return "GetAdminRawFile";
            case "splitfile":
                return "GetAdminSplitFile";
            case "exportfile":
                return "GetAdminExportFile";
            default:
                throw new Error("Invalid fileType");
        }
    }

    function DownloadFileContent(fileType, paymentId) {
        try {
            setIsLoading(true);
            let methodName = GetDownloadFileControllerName(fileType);

            let accessToken = passedProps.auth.getAccessToken();

            let parameters = {
                paymentId: paymentId,
            };

            axios
                .get(`api/RemittanceLookup/${methodName}`, {
                    params: parameters,
                    headers: { Authorization: `Bearer ${accessToken}` },
                })
                .then((response) => {
                    let fileName = response.data.fileName;
                    let downloadBytes = Base64ToArrayBuffer(response.data.fileStream);
                    setIsLoading(false);
                    SaveByteArray(fileName, downloadBytes);
                })
                .catch((error) => onDownloadFileFailure(fileType));
        } catch (exception) {
            onDownloadFileFailure(fileType);
        }
    }

    const downloadFileCell = (props) => {

        function DownloadFile() {
            let paymentId = props.dataItem["pmid"];

            let fileType = "rawfile";

            if (props.secondaryField === "exportFile") {
                fileType = "exportFile";
            } else if (props.secondaryField === "splitFile") {
                fileType = "splitfile";
            } else if (props.secondaryField === "rawFile") {
                fileType = "rawfile";
            }
            // console.log(paymentId);
            // console.log(fileType);

            DownloadFileContent(fileType, paymentId);
        }

        if (props.secondaryField === "exportFile") {
            return (
                <td {...props.tdProps} style={{ ...props.style }}>
                    {!(
                        props.dataItem["batchcompleted"] === "X" ||
                        props.dataItem["batchcompleted"] === null ||
                        props.dataItem["batchcompleted"] === "0"
                    ) ? (
                        <span style={{ cursor: "pointer" }} onClick={DownloadFile}>
                            <i className="fa fa-download" aria-hidden="true"></i>{" "}
                        </span>
                    ) : (
                        ""
                    )}{" "}
                </td>
            );
        } else {
            return (
                <td {...props.tdProps} style={{ ...props.style }}>
                    {props.dataItem[props.field] ? (
                        <span style={{ cursor: "pointer" }} onClick={DownloadFile}>
                            <i className="fa fa-download" aria-hidden="true"></i>{" "}
                        </span>
                    ) : (
                        ""
                    )}{" "}
                </td>
            );
        }
    };

    const exportStatusCell = (props) => {
        function ShowAlertMessage() {
            alert(props.dataItem[props.field]);
        }
        return (
            <td {...props.tdProps} style={{ ...props.style }}>
                {props.dataItem[props.field] === "Completed" ||
                    props.dataItem[props.field] === null ? (
                    <span>{props.dataItem[props.field]}</span>
                ) : (
                    <span className="hyperlinkStyle" onClick={ShowAlertMessage}>
                        Error
                    </span>
                )}
            </td>
        );
    };

    function onFailure(error) {
        LogError(error);

        switch (error.response.config.url || error.config.url) {
            case "api/RemittanceLookup/GetAdminRemittanceReport":
                setError(error.response);
                break;
            case "api/RemittanceLookup/AdminRemittanceSuppressChecks":
                setError(error.response || error);
                break;
            case "api/RemittanceLookup/AdminRemittanceUnsuppressChecks":
                setError(error.response || error);
                break;
            default:
                // Handle cases where URL might not be available (e.g., network errors)
                setError(error.response || error);
                break;
        }
        setIsLoading(false);
        return;
    }

    const sendAggregatesToParent = (aggregatesFromSharedMainAgingGrid) => {
        setMainGridAggregates(aggregatesFromSharedMainAgingGrid.data);
    };

    useEffect(() => {
        refrehGridActiveColors();
    }, [mainGridAggregates]);

    const AggregateMainGridMoneyFooterCell = (props) => {
        var options = {
            aggregateData: mainGridAggregates,
        };

        return AggregateMoneyFooterCell(props, options);
    };

    function setMainGridDataSource(allData) {
        if (allData) {
            setMainGridData(allData);
        }
    }

    return (
        <React.Fragment>
            <div className="feature">
                <LoadingSpinner
                    isDataLoading={isLoading || suppressMutation.isPending || unsuppressMutation.isPending}
                    controlsName={"AdminRemittanceLookup"}
                />
                <ErrorHandler
                    error={error}
                    onClose={() => {
                        setError({});
                    }}
                />
                <Accordion
                    defaultExpanded
                    label=""
                    sendAccordionParentCallback={sendAccordionParent}
                >
                    <Row>
                        <Col>
                            <AdminRemittanceLookupForm
                                passedProps={passedProps}
                                handleFormSubmit={handleFormSubmit}
                                handleFormReset={handleFormReset}
                                enabledButton={mainGridData.length > 0}
                            />
                        </Col>
                    </Row>
                </Accordion>
                <div ref={_container}>
                    <Row>
                        <Col className="tight-grid multi-line-filter">
                            <SharedMainSelectableClientGrid
                                ref={clientGridRef}
                                isSelectable={true}
                                apiGetEndpoint={"api/RemittanceLookup/GetAdminRemittanceReport"}
                                apiParameters={gridApiParams}
                                dataItemKey={DATA_ITEM_KEY}
                                aggregateColumnSettings={[{ aggregate: "sum", field: "checkAmount" }]}
                                sendAggregatesParentCallback={sendAggregatesToParent}
                                exportFileNamePrefix={"RemittanceLookup"}
                                gridButtons={gridButtons}
                                onDataStateChangedCallback={() => {
                                    const allData = clientGridRef.current
                                        ? clientGridRef.current.GetAllGridData()
                                        : [];

                                    if (allData) {
                                        setMainGridDataSource(allData);
                                    }
                                }}
                                gridDataStateResetProcessingInitiated={
                                    gridDataStateResetProcessingInitiated
                                }
                                onGridDataStateResetCompleted={onGridDataStateResetCompleted}
                            >
                                <GridColumn
                                    key="payerName"
                                    filter="text"
                                    field="payerName"
                                    title="Payer"
                                    width={DefaultColumnWidth()}
                                />
                                <GridColumn
                                    key="checkNumber"
                                    filter="text"
                                    field="checkNumber"
                                    title="Check Number"
                                    width={DefaultColumnWidth()}
                                />
                                <GridColumn
                                    key="checkAmount"
                                    filter="numeric"
                                    field="checkAmount"
                                    title="Amount"
                                    width={DefaultColumnWidth()}
                                    cells={{
                                        data: MoneyCell,
                                        footerCell: AggregateMainGridMoneyFooterCell,
                                    }}
                                    footerCell={AggregateMainGridMoneyFooterCell}
                                />
                                <GridColumn
                                    key="checkDate"
                                    filter="date"
                                    field="checkDate"
                                    title="Check Date"
                                    format="{0:d}"
                                    width={DefaultDateColumnWidth()}
                                />
                                <GridColumn
                                    key="remitDate"
                                    filter="date"
                                    field="remitDate"
                                    title="Remit Date"
                                    format="{0:d}"
                                    width={DefaultDateColumnWidth()}
                                />
                                <GridColumn
                                    key="paymentDate"
                                    filter="date"
                                    field="paymentDate"
                                    title="Payment Date"
                                    format="{0:d}"
                                    width={DefaultDateColumnWidth()}
                                />
                                <GridColumn
                                    key="archiveDate"
                                    filter="date"
                                    field="archiveDate"
                                    title="Archive Date"
                                    format="{0:d}"
                                    width={DefaultDateColumnWidth()}
                                />
                                <GridColumn
                                    key="loadDate"
                                    filter="date"
                                    field="loadDate"
                                    title="Load Date"
                                    format="{0:d}"
                                    width={DefaultDateColumnWidth()}
                                />
                                <GridColumn
                                    key="ncpdp"
                                    filter="text"
                                    field="ncpdp"
                                    title="NCPDP"
                                    width={DefaultColumnWidth()}
                                />
                                <GridColumn
                                    key="batchId"
                                    filter="text"
                                    field="batchId"
                                    title="BatchID"
                                    width={DefaultColumnWidth()}
                                />
                                <GridColumn
                                    key="exportDate"
                                    filter="date"
                                    field="exportDate"
                                    title="Export Date"
                                    format="{0:d}"
                                    width={DefaultDateColumnWidth()}
                                />
                                <GridColumn
                                    key="exportStatus"
                                    filter="text"
                                    field="exportStatus"
                                    title="Export Status"
                                    width={DefaultColumnWidth()}
                                    cells={{ data: exportStatusCell }}
                                />
                                <GridColumn
                                    key="suppressed"
                                    field="suppressed"
                                    title="Suppressed"
                                    filter="boolean"
                                    width={DefaultBooleanOrShortWidthColumnWidth()}
                                />
                                <GridColumn
                                    key="exportFile"
                                    filterable={false}
                                    field="exportFile"
                                    title="Export File"
                                    cells={{
                                        data: (props) => downloadFileCell({
                                            ...props,
                                            secondaryField: "exportFile"
                                        }),
                                    }}
                                    width={DefaultNoFilterColumnWidth()}
                                />
                                <GridColumn
                                    key="splitFilename"
                                    filterable={false}
                                    field="filename"
                                    title="Split File"
                                    cells={{
                                        data: (props) => downloadFileCell({
                                            ...props,
                                            secondaryField: "splitFile"
                                        }),
                                    }}
                                    width={DefaultNoFilterColumnWidth()}
                                />
                                <GridColumn
                                    key="rawFilename"
                                    filterable={false}
                                    field="filename"
                                    title="Raw File"
                                    cells={{
                                        data: (props) => downloadFileCell({
                                            ...props,
                                            secondaryField: "rawFile"
                                        }),
                                    }}
                                    width={DefaultNoFilterColumnWidth()}
                                />
                            </SharedMainSelectableClientGrid>
                        </Col>
                    </Row>
                </div>
                <ZIndexContext.Provider value={10005}>
                    <Modal
                        show={reloadCheckConfirmation}
                        onHide={() => {
                            setReloadCheckConfirmationModel(false);
                        }}
                    >
                        <Modal.Header closeButton>
                            <h4>Reload Check</h4>
                        </Modal.Header>
                        <Modal.Body>
                            You are about to Reload Check, Press Yes to Continue
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="primary"
                                onClick={handleReloadCheckConfirmationYesButton}
                            >
                                Yes
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setReloadCheckConfirmationModel(false);
                                }}
                            >
                                No
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </ZIndexContext.Provider>
                <ZIndexContext.Provider value={10002}>
                    <Modal
                        show={deleteCheckConfirmation}
                        onHide={() => {
                            setDeleteCheckConfirmationModel(false);
                        }}
                    >
                        <Modal.Header closeButton>
                            <h4>Delete Check</h4>
                        </Modal.Header>
                        {clientGridRef.current && 
                        clientGridRef.current.GetSelectedGridDataKeys() && 
                        clientGridRef.current.GetSelectedGridDataKeys().length > 0 ? (
                            <Modal.Body>
                                Are you sure you want to delete the selected {clientGridRef.current.GetSelectedGridDataKeys().length} check(s)?
                            </Modal.Body>
                        ) : (
                            <Modal.Body>Do you want to delete selected checks?</Modal.Body>
                        )}
                        <Modal.Footer>
                            <Button
                                variant="primary"
                                onClick={handleDeleteCheckConfirmationYesButton}
                            >
                                Yes
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setDeleteCheckConfirmationModel(false);
                                }}
                            >
                                No
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </ZIndexContext.Provider>
                <ZIndexContext.Provider value={10003}>
                    <Modal
                        show={suppressCheckConfirmation}
                        onHide={() => {
                            setSuppressCheckConfirmationModel(false);
                        }}
                    >
                        <Modal.Header closeButton>
                            <h4>Suppress Check</h4>
                        </Modal.Header>
                        <Modal.Body>Do you want to suppress selected checks?</Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="primary"
                                onClick={handleSuppressCheckConfirmationYesButton}
                            >
                                Yes
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setSuppressCheckConfirmationModel(false);
                                }}
                            >
                                No
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </ZIndexContext.Provider>
                <ZIndexContext.Provider value={10004}>
                    <Modal
                        show={unsuppressCheckConfirmation}
                        onHide={() => {
                            setUnsuppressCheckConfirmationModel(false);
                        }}
                    >
                        <Modal.Header closeButton>
                            <h4>Unsuppress Check</h4>
                        </Modal.Header>
                        <Modal.Body>Do you want to unsuppress selected checks?</Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="primary"
                                onClick={handleUnSuppressCheckConfirmationYesButton}
                            >
                                Yes
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setUnsuppressCheckConfirmationModel(false);
                                }}
                            >
                                No
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </ZIndexContext.Provider>
            </div>
        </React.Fragment>
    );
}
